import { SURFACES, SoundActor, StepCadence, crowdAdmits, footstepHook, withinStepReach } from "odyssey-shared";
import type { PlaySound } from "./cues.ts";

/**
 * The page's footsteps (ADR-085's amendment for P1.9-T84): the page's own character's steps on Player and every
 * other player's on Other players, each on the surface under its feet, as its cadence says a step falls. Another
 * player's step beyond the reach of another player's sounds is never handed to the mixer, and a crowd's steps are
 * held to one each `OTHERS_STEP_GAP_MS`, so two hundred players walking past start no more sounds than a dozen a
 * second. Nothing here allocates as a frame is drawn: the hooks are named once and a cadence is a few numbers.
 */

/**
 * The surface under a point of the map, in sixteenths of a tile.
 */
export type SurfaceAt = (x: number, y: number) => string;

const HOOKS: ReadonlyMap<string, string> = new Map(SURFACES.map((surface) => [surface.id, footstepHook(surface.id)]));

/**
 * The step hook of a surface, named once for every listed surface.
 *
 * @param surface The surface's id.
 * @returns The hook.
 */
function hookOf(surface: string): string {
  return HOOKS.get(surface) ?? footstepHook(surface);
}

/**
 * The page's footsteps.
 */
export class Footsteps {
  private readonly own = new StepCadence();
  private lastOther = Number.NEGATIVE_INFINITY;

  /**
   * @param play Plays a hook.
   * @param surfaceAt The surface under a point of the loaded map.
   */
  public constructor(
    private readonly play: PlaySound,
    private readonly surfaceAt: SurfaceAt,
  ) {}

  /**
   * Forgets the page's own cadence and the crowd's last step, as a session starts or ends.
   */
  public reset(): void {
    this.own.reset();
    this.lastOther = Number.NEGATIVE_INFINITY;
  }

  /**
   * The page's own character, each frame it is drawn: a step falling plays on Player, not placed.
   *
   * @param x Where it is rendered across, in sixteenths of a tile.
   * @param y The same down.
   * @param walking Whether it walks: moving, alive and not dashing.
   * @param now The time in milliseconds.
   * @param running Whether it is drawn running, which takes the run's stride; false when not given.
   */
  public ownFrame(x: number, y: number, walking: boolean, now: number, running = false): void {
    if (this.own.advance(x, y, walking, now, running)) {
      this.play(hookOf(this.surfaceAt(x, y)), SoundActor.Own);
    }
  }

  /**
   * Another player, each frame it is drawn: a step falling within reach, and admitted by the crowd's gap, plays on
   * Other players from where it stands.
   *
   * @param cadence The player's cadence, kept with its drawn character.
   * @param x Where it is drawn across, in sixteenths of a tile.
   * @param y The same down.
   * @param walking Whether it walks.
   * @param now The time in milliseconds.
   * @param dx Its offset across from the page's character, in tiles.
   * @param dy The offset down, in tiles.
   * @param running Whether it is drawn running, which takes the run's stride; false when not given.
   */
  public otherFrame(cadence: StepCadence, x: number, y: number, walking: boolean, now: number, dx: number, dy: number, running = false): void {
    if (!cadence.advance(x, y, walking, now, running) || !withinStepReach(dx, dy) || !crowdAdmits(now, this.lastOther)) {
      return;
    }
    this.lastOther = now;
    this.play(hookOf(this.surfaceAt(x, y)), SoundActor.Player, dx, dy);
  }
}
